import {
  POOL_CAPTURE_SECONDS, POOL_DECAY_PER_SEC, POOL_INNER_RADIUS,
  SHRINE_DRAIN_PER_SEC, SHRINE_INNER_RADIUS, SHRINE_POWER_MAX, SHRINE_RESTORE_PER_SEC,
  LEY_POOL_RANGE, LEY_SHRINE_RANGE, OBJECTIVE_HEIGHT_TOLERANCE,
  type Faction,
} from './constants.ts';
import { CLASSES } from './classes.ts';
import { holdsObjectives } from './player.ts';
import type { PlayerState } from './player.ts';
import { dist2d, type Vec3 } from './math.ts';

export type Owner = Faction | 'neutral';

export interface Pool {
  id: string;
  name: string;
  pos: Vec3;
  owner: Owner;
  /** Which faction the current partial progress belongs to. */
  progressFaction: Owner;
  /** 0..1 toward progressFaction owning it. */
  progress: number;
  regenRange: number;
  innerRadius: number;
}

export interface Shrine {
  id: string;
  faction: Faction;
  pos: Vec3;
  /** Where this faction's players return after Valhalla. */
  respawn: Vec3;
  /** 0..100. At zero the shrine is conquered and its jewel greys out. */
  power: number;
  conquered: boolean;
  regenRange: number;
  innerRadius: number;
}

export interface ValhallaZone {
  pos: Vec3;
  radius: number;
  /** Where the escape teleporter drops you. Empty means a random arena spawn. */
  exits: Vec3[];
}

export interface Arena {
  name: string;
  pools: Pool[];
  shrines: Shrine[];
  valhalla: ValhallaZone[];
  spawns: Record<Faction, Vec3[]>;
}

/**
 * @param innerRadius How close you must stand to bias it. Defaults to the house
 *   figure; an authored arena passes the *inscribed* radius of the shape it drew,
 *   so a pool never claims ground its water does not cover. Godot's pools are
 *   rectangles and core's are circles, and taking half the shorter side is the
 *   reading that cannot lie in the player's favour.
 */
export function makePool(
  id: string, name: string, pos: Vec3, innerRadius = POOL_INNER_RADIUS,
): Pool {
  return {
    id, name, pos,
    owner: 'neutral',
    progressFaction: 'neutral',
    progress: 0,
    regenRange: LEY_POOL_RANGE,
    innerRadius,
  };
}

export function makeShrine(faction: Faction, pos: Vec3, respawn: Vec3): Shrine {
  return {
    id: `shrine_${faction}`,
    faction, pos, respawn,
    power: SHRINE_POWER_MAX,
    conquered: false,
    regenRange: LEY_SHRINE_RANGE,
    innerRadius: SHRINE_INNER_RADIUS,
  };
}

/** Distinct factions of players who count toward contesting a zone. */
function contestingFactions(
  players: PlayerState[],
  centre: Vec3,
  radius: number,
  predicate: (p: PlayerState) => boolean,
): Faction[] {
  const out: Faction[] = [];
  for (const p of players) {
    if (!holdsObjectives(p)) continue;
    if (!predicate(p)) continue;
    if (dist2d(p.pos, centre) > radius) continue;
    // Horizontal distance alone would hand the Crypt pool to anyone standing on
    // the gallery four and a half metres above it.
    if (Math.abs(p.pos.y - centre.y) > OBJECTIVE_HEIGHT_TOLERANCE) continue;
    if (!out.includes(p.faction)) out.push(p.faction);
  }
  return out;
}

export interface ArenaEvent {
  type: 'pool-biased' | 'shrine-conquered' | 'shrine-restored';
  faction: Faction;
  /** The faction that lost control, where one applies. */
  from?: Owner;
  id: string;
  /** Players who were standing in the zone when it flipped, for experience. */
  credit: string[];
}

/**
 * Advances every pool and shrine one step.
 *
 * Pools: presence biases them. One faction present and progress climbs; two or
 * more and it freezes; nobody and an unfinished capture slowly fades. Arcanists
 * are invisible to this entirely — they cannot impose their will on a pool.
 *
 * Shrines: enemies standing inside drain power, the owners standing inside
 * restore it. At zero the shrine is conquered, which cuts off both respawns and
 * the faction Cleric's power supply. Owners can bring it back up.
 * Magicians cannot conquer shrines.
 */
export function tickArena(arena: Arena, players: PlayerState[], dt: number): ArenaEvent[] {
  const events: ArenaEvent[] = [];

  for (const pool of arena.pools) {
    const inZone = players.filter(
      (p) => holdsObjectives(p) && CLASSES[p.cls].canBiasPools &&
        dist2d(p.pos, pool.pos) <= pool.innerRadius,
    );
    const present = contestingFactions(players, pool.pos, pool.innerRadius,
      (p) => CLASSES[p.cls].canBiasPools);

    if (present.length === 1) {
      const f = present[0];
      const rate = dt / POOL_CAPTURE_SECONDS;

      if (pool.owner === 'neutral') {
        if (pool.progressFaction !== f && pool.progress > 0) {
          // Somebody else's partial claim has to be wiped first.
          pool.progress = Math.max(0, pool.progress - rate);
          if (pool.progress === 0) pool.progressFaction = f;
        } else {
          pool.progressFaction = f;
          pool.progress = Math.min(1, pool.progress + rate);
          if (pool.progress === 1) {
            pool.owner = f;
            events.push({ type: 'pool-biased', faction: f, from: 'neutral', id: pool.id, credit: inZone.map((p) => p.id) });
          }
        }
      } else if (pool.owner === f) {
        pool.progress = Math.min(1, pool.progress + rate);
      } else {
        const lost = pool.owner;
        pool.progress = Math.max(0, pool.progress - rate);
        if (pool.progress === 0) {
          pool.owner = 'neutral';
          pool.progressFaction = f;
          events.push({ type: 'pool-biased', faction: f, from: lost, id: pool.id, credit: [] });
        }
      }
    } else if (present.length === 0 && pool.owner === 'neutral' && pool.progress > 0) {
      pool.progress = Math.max(0, pool.progress - POOL_DECAY_PER_SEC * dt);
    }
    // Two or more factions present: contested, everything frozen.
  }

  for (const shrine of arena.shrines) {
    const inZone = players.filter(
      (p) => holdsObjectives(p) && CLASSES[p.cls].canConquerShrines &&
        dist2d(p.pos, shrine.pos) <= shrine.innerRadius,
    );
    const attackers = inZone.filter((p) => p.faction !== shrine.faction);
    const defenders = inZone.filter((p) => p.faction === shrine.faction);

    if (attackers.length > 0 && defenders.length === 0) {
      shrine.power = Math.max(0, shrine.power - SHRINE_DRAIN_PER_SEC * dt);
      if (shrine.power === 0 && !shrine.conquered) {
        shrine.conquered = true;
        events.push({
          type: 'shrine-conquered',
          faction: attackers[0].faction,
          from: shrine.faction,
          id: shrine.id,
          credit: attackers.map((p) => p.id),
        });
      }
    } else if (defenders.length > 0 && attackers.length === 0) {
      const was = shrine.conquered;
      shrine.power = Math.min(SHRINE_POWER_MAX, shrine.power + SHRINE_RESTORE_PER_SEC * dt);
      if (was && shrine.power > 0) {
        shrine.conquered = false;
        events.push({ type: 'shrine-restored', faction: shrine.faction, id: shrine.id, credit: defenders.map((p) => p.id) });
      }
    }
    // Both sides present: contested, power frozen.
  }

  return events;
}

export function shrineOf(arena: Arena, faction: Faction): Shrine | undefined {
  return arena.shrines.find((s) => s.faction === faction);
}

export function canFactionRespawn(arena: Arena, faction: Faction): boolean {
  const s = shrineOf(arena, faction);
  return !!s && !s.conquered;
}

// ── What a pool says it is ───────────────────────────────────────────────────

/**
 * How a pool reads to somebody standing in it.
 *
 * The floor circle says the same thing, and says it in a place a player is not
 * looking: down, in the middle of a fight, at a ring under their own feet. This
 * is the same fact put where the eye already is.
 *
 * Kept in core because it is a *rule*, not a decoration — what the pool is, who
 * is taking it and whether this particular mage can take it at all are the same
 * questions the simulation answers when it decides whether the bar moves. Two
 * implementations would eventually disagree, and the one on screen would be the
 * one people believed.
 */
export type PoolTone = Faction | 'neutral';

export interface PoolReadout {
  text: string;
  /** Which colour to say it in. */
  tone: PoolTone;
  /** 0–100, as shown. */
  percent: number;
}

const TITLE: Record<Faction, string> = {
  order: 'Order',
  balance: 'Balance',
  chaos: 'Chaos',
};

export function poolReadout(
  pool: Pool, viewer: PlayerState, players: PlayerState[],
): PoolReadout {
  const percent = Math.round(pool.progress * 100);

  /*
   * The colour follows the *claim*, not the owner.
   *
   * While a pool is being taken off Chaos it stays red down to nothing, because
   * it is still theirs until it reaches zero. The moment it does, the ownership
   * is gone and the partial claim underneath belongs to whoever is standing
   * there — so it turns grey at nothing and then takes their colour on the first
   * point of the way back up. That reads as a fight being won.
   */
  const tone: PoolTone = pool.owner !== 'neutral'
    ? pool.owner
    : (pool.progress > 0 ? pool.progressFaction : 'neutral');

  /*
   * "Biasing" only when the bar is actually moving.
   *
   * A Magician standing in an enemy pool with an enemy Magician is not taking
   * it — everything is frozen while two sides are present, and telling them they
   * are making progress would be a lie they could watch not happening. So this
   * asks the same question the simulation does: is exactly one biasing faction
   * here, and is it theirs?
   */
  const biasing = contestingFactions(
    players, pool.pos, pool.innerRadius, (p) => CLASSES[p.cls].canBiasPools,
  );
  const mine = biasing.length === 1 && biasing[0] === viewer.faction
    && CLASSES[viewer.cls].canBiasPools;

  /*
   * "Biasing" only while there is something left to bias.
   *
   * A Magician standing in a pool their own side already holds outright is not
   * taking it — there is nothing to take — and a bar that says otherwise at a
   * hundred per cent for as long as they stand there is a progress report on
   * nothing. Once it is theirs, it simply says so.
   */
  const alreadyHeld = pool.owner === viewer.faction && pool.progress >= 1;

  if (mine && !alreadyHeld) {
    return { text: `Biasing pool to ${TITLE[viewer.faction]}… ${percent}%`, tone, percent };
  }

  if (pool.owner !== 'neutral') {
    return { text: `Pool biased to ${TITLE[pool.owner]} ${percent}%`, tone, percent };
  }

  return { text: `Pool unbiased ${percent}%`, tone, percent };
}

/** The pool a mage is standing in, if any. */
export function poolUnderfoot(arena: Arena, p: PlayerState): Pool | null {
  for (const pool of arena.pools) {
    if (dist2d(p.pos, pool.pos) <= pool.innerRadius) return pool;
  }
  return null;
}
